import React from 'react'
import classNames from 'classnames'
import { TextField, CircleLoader } from '@deal/components'
import { UserAgentConsumer } from '#src/containers/UserAgent'
import FacebookShare from '../FacebookShare'
import EmailCollectionModal from '../EmailCollectionModal'
import TextModal from '../TextModal'
import { UAParser } from 'ua-parser-js'
import { isMobileBrowser } from '#src/services/mobile'
import SMSIcon from '../share-sms.svg'
import FacebookIcon from '../share-facebook.svg'
import LinkIcon from '../share-link.svg'
import EmailIcon from '../share-email.svg'
import smsLink from '#src/services/smsLink'
import styles from './styles.css'
import qs from 'qs'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { generateReferralLink } from '#src/services/tracking'
import { CreateShareLinkComponent, CreateShareLinkMutation } from '#src/generated/graphql'
import { toast } from 'react-toastify'
import config from '#src/config'
import { ExecutionResult } from 'graphql'

type ShareTerm = 'product' | 'experience' | 'expert' | 'article' | 'topic'

type ShareMedium = 'sms' | 'link' | 'email' | 'fb'

type ShareVariant = 'small' | 'large' | 'responsive'

interface ShareProps extends RouteComponentProps<{}> {
  type: ShareTerm
  variant: ShareVariant
  sellableId?: string
  expertId?: string
  utmContent: string
  expertVanityUrl?: string | null
  className?: string
  sharePath?: string
}

interface ShareState {
  isMobile: boolean
  emailModalOpened: boolean
  linkModalOpened: boolean
  smsModalOpened: boolean
  smsInputText: string
  shortenedUrl?: string
}

interface IconBoxProps {
  onClick: () => void
  loading?: boolean
  variant: ShareVariant
}

const FacebookIconBox: React.SFC<IconBoxProps> = ({ variant, onClick }) => {
  return (
    <div
      className={classNames(styles.facebookBox, {
        [styles.responsiveShareBox]: variant === 'responsive',
        [styles.largeShareBox]: variant === 'large',
        [styles.smallShareBox]: variant === 'small'
      })}
      onClick={onClick}
    >
      <FacebookIcon />
    </div>
  )
}

const EmailIconBox: React.SFC<IconBoxProps> = ({ variant, onClick }) => {
  return (
    <div
      className={classNames(styles.emailBox, {
        [styles.responsiveShareBox]: variant === 'responsive',
        [styles.largeShareBox]: variant === 'large',
        [styles.smallShareBox]: variant === 'small'
      })}
      onClick={onClick}
    >
      <EmailIcon />
    </div>
  )
}

const LinkIconBox: React.SFC<IconBoxProps> = ({ variant, loading, onClick }) => {
  return (
    <div
      className={classNames(styles.linkBox, {
        [styles.responsiveShareBox]: variant === 'responsive',
        [styles.largeShareBox]: variant === 'large',
        [styles.smallShareBox]: variant === 'small'
      })}
      onClick={onClick}
    >
      {loading ? <CircleLoader size="12" color="#fff" /> : <LinkIcon />}
    </div>
  )
}

const SMSIconBox: React.SFC<IconBoxProps> = ({ variant, loading, onClick }) => {
  return (
    <div
      className={classNames(styles.smsBox, {
        [styles.responsiveShareBox]: variant === 'responsive',
        [styles.largeShareBox]: variant === 'large',
        [styles.smallShareBox]: variant === 'small'
      })}
      onClick={onClick}
    >
      {loading ? <CircleLoader size="12" color="#fff" /> : <SMSIcon />}
    </div>
  )
}

class Share extends React.Component<
  ShareProps & WithAnalyticsProps & WithIdentityProps,
  ShareState
> {
  constructor(props: ShareProps) {
    super(props)

    this.state = {
      isMobile: false,
      emailModalOpened: false,
      linkModalOpened: false,
      smsModalOpened: false,
      smsInputText: '',
      shortenedUrl: ''
    }

    this.updateShareUrl = this.updateShareUrl.bind(this)
    this.onOpenLinkModal = this.onOpenLinkModal.bind(this)
    this.onCloseLinkModal = this.onCloseLinkModal.bind(this)
    this.onOpenSMSModal = this.onOpenSMSModal.bind(this)
    this.onCloseSMSModal = this.onCloseSMSModal.bind(this)
    this.onOpenEmailModal = this.onOpenEmailModal.bind(this)
    this.onCloseEmailModal = this.onCloseEmailModal.bind(this)
  }

  public onOpenLinkModal() {
    this.setState({
      linkModalOpened: true
    })
  }

  public onCloseLinkModal() {
    this.setState({
      linkModalOpened: false
    })
  }

  public onOpenEmailModal() {
    this.setState({
      emailModalOpened: true
    })
  }

  public onCloseEmailModal() {
    this.setState({
      emailModalOpened: false
    })
  }

  public onOpenSMSModal() {
    this.setState({
      smsModalOpened: true
    })
  }

  public onCloseSMSModal() {
    this.setState({
      smsModalOpened: false
    })
  }

  public onChangeSMSText = (event: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({ smsInputText: event.target.value })

  public onSendSMS = () => {
    const { smsInputText } = this.state
    const parser = new UAParser()
    const os = parser.getOS()

    this.setState({ smsModalOpened: false }, () => {
      window.location.replace(
        smsLink(
          os,
          smsInputText,
          `Hey, check out what I found on Curated.com. ${this.state.shortenedUrl}`
        )
      )
    })
  }

  public generateShareParams(term: ShareTerm, medium: ShareMedium) {
    const { sellableId, expertId } = this.props
    const shareObject = {
      utm_source: 'referral',
      utm_campaign: sellableId
        ? 'consumer-sharing-product'
        : expertId ? 'consumer-sharing-expert' : null,
      utm_id: sellableId ? sellableId : expertId ? expertId : null,
      utm_term: term,
      utm_adgroup: medium,
      utm_content: this.props.utmContent
    }

    return qs.stringify(shareObject)
  }

  public generateReferralLinkFromUrlOrProps(medium: ShareMedium) {
    const sharePath = this.props.sharePath
      ? this.props.sharePath
      : `${this.props.history.location.pathname}${this.props.history.location.search}`

    /**
     * Share urls with a referral link can only be generated if a
     * user ID is present (logged in) or we're on an expert profile page
     */
    if (this.props.myself || this.props.expertId) {
      return generateReferralLink(
        `${config.get('openGraph.url')}${sharePath}`,
        this.props.myself ? this.props.myself.id : '',
        this.props.expertId,
        this.generateShareParams(this.props.type, medium)
      )
    } else {
      return `${config.get('openGraph.url')}${sharePath}`
    }
  }

  public updateShareUrl(
    response: void | ExecutionResult<CreateShareLinkMutation>,
    callback: () => void
  ) {
    if (response && response.data && response.data.shortenUrl && response.data.shortenUrl.url) {
      this.setState(
        {
          shortenedUrl: response.data.shortenUrl.url
        },
        callback
      )
    } else {
      toast.error('Error generating referral url, please try again.')
    }
  }

  public render() {
    const { smsModalOpened, linkModalOpened, emailModalOpened, smsInputText } = this.state
    const { className, variant, expertId } = this.props

    return (
      <div>
        <div className={classNames(styles.shareContainer, className)}>
          <CreateShareLinkComponent
            variables={{
              input: {
                url: this.generateReferralLinkFromUrlOrProps('link'),
                preferredDomain: 'curatedrefer.com'
              }
            }}
          >
            {(createShareurl, { loading }) => (
              <LinkIconBox
                loading={loading}
                variant={variant}
                onClick={() => {
                  if (this.state.shortenedUrl) {
                    this.onOpenLinkModal()
                  } else {
                    createShareurl().then(response => {
                      this.updateShareUrl(response, this.onOpenLinkModal)
                    })
                  }
                }}
              />
            )}
          </CreateShareLinkComponent>

          <CreateShareLinkComponent
            variables={{
              input: {
                url: this.generateReferralLinkFromUrlOrProps('email'),
                preferredDomain: 'curatedrefer.com'
              }
            }}
          >
            {(createShareurl, { loading }) => (
              <UserAgentConsumer>
                {ua =>
                  // the sms prefill link does not work in our native WKWebView
                  isMobileBrowser(ua) && (
                    <SMSIconBox
                      loading={loading}
                      variant={variant}
                      onClick={() => {
                        if (this.state.shortenedUrl) {
                          this.onOpenSMSModal()
                        } else {
                          createShareurl().then(response => {
                            this.updateShareUrl(response, this.onOpenSMSModal)
                          })
                        }
                      }}
                    />
                  )
                }
              </UserAgentConsumer>
            )}
          </CreateShareLinkComponent>

          {expertId && (
            <EmailIconBox
              variant={variant}
              onClick={() => {
                this.onOpenEmailModal()
              }}
            />
          )}

          <FacebookShare shareUrl={this.generateReferralLinkFromUrlOrProps('fb')}>
            {shareOnFacebook => (
              <FacebookIconBox
                variant={variant}
                onClick={() => {
                  shareOnFacebook()
                }}
              />
            )}
          </FacebookShare>
        </div>
        {smsModalOpened && (
          <TextModal
            isOpen={true}
            onCloseModal={this.onCloseSMSModal}
            content="Share with a friend via text!"
            onSend={this.onSendSMS}
            input={
              <TextField
                display="standard"
                label=""
                labelHidden={true}
                type="tel"
                onChange={this.onChangeSMSText}
                value={smsInputText}
                placeholder="Enter a phone number"
              />
            }
          />
        )}

        {linkModalOpened && (
          <TextModal
            isOpen={true}
            onCloseModal={this.onCloseLinkModal}
            content="Share with a friend via the link below!"
            onSend={this.onCloseLinkModal}
            buttonText={'Okay!'}
            input={
              <TextField
                display="standard"
                label=""
                labelHidden={true}
                value={this.state.shortenedUrl}
              />
            }
          />
        )}
        {emailModalOpened &&
          expertId && (
            <EmailCollectionModal onRequestClose={this.onCloseEmailModal} expertId={expertId} />
          )}
      </div>
    )
  }
}

export default withIdentity(withRouter(withAnalytics(Share)))
